/* eslint-disable react/no-danger */
import { useState, useEffect, useCallback } from "react";
import {
  Paper,
  Group,
  Text,
  ActionIcon,
  Collapse,
  Stack,
  Button,
  Badge,
  Loader,
  Alert,
  Timeline,
  Tooltip,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronUp,
  IconFileText,
  IconPlus,
  IconEdit,
  IconCheck,
  IconAlertCircle,
  IconTrash,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import { notifications } from "@mantine/notifications";
import {
  getSignOffHistory,
  type SignOffHistoryItem,
} from "../../services/signOff.service";
import { stripHtml } from "../../utils/helpers";
import UpdatePatientInfoModal from "../modals/UpdatePatientInfoModal";
import { resolveVariableChipValues } from "../shared/tiptap/VariableChip";
import {
  useAddendumDraft,
  useDeleteAddendumDraft,
} from "../../hooks/useAddendum";
import {
  useAmendmentDraft,
  useDeleteAmendmentDraft,
} from "../../hooks/useAmendment";
import { ConfirmAddendumActionModal } from "./ConfirmAddendumActionModal";

function resolveCommentsHtml(comments: string): string {
  if (!comments) return comments;
  let result = resolveVariableChipValues(comments);
  result = result.replace(
    /<span[^>]*data-variable="([^"]+)"[^>]*>.*?<\/span>/g,
    (_m, varName: string) => varName
  );
  result = result.replace(/\{\{(\w+)\}\}/g, (_m, varName: string) => varName);
  return result;
}

function shouldShowHistoryReason(entry: SignOffHistoryItem): boolean {
  if (!entry.amendmentReason) return false;
  if (entry.signOffType !== "amended") return true;
  const reason = stripHtml(
    resolveCommentsHtml(entry.addendumName || entry.amendmentReason)
  )
    .replace(/\s+/g, " ")
    .trim();
  const body = stripHtml(resolveCommentsHtml(entry.comments || ""))
    .replace(/\s+/g, " ")
    .trim();
  return !reason || !body || reason !== body;
}

interface AddendumSectionProps {
  caseId: string | null;
  isFinalized: boolean;
  requireAddendumFirst?: boolean;
  reportVersion?: number;
  onAmendmentCreated?: () => void;
  onAddAddendum?: () => void;
  onAddAmendment?: () => void;
  onContinueAmendment?: () => void;
  caseData?: any;
}

function getTypeConfig(type: string): {
  label: string;
  color: string;
  icon: React.ReactNode;
} {
  switch (type) {
    case "initial":
      return {
        label: "Original Sign-Off",
        color: "green",
        icon: <IconCheck size={12} />,
      };
    case "corrected":
      return {
        label: "CORRECTED",
        color: "blue",
        icon: <IconEdit size={12} />,
      };
    case "addendum":
      return {
        label: "ADDENDUM",
        color: "cyan",
        icon: <IconPlus size={12} />,
      };
    case "amended":
    case "amendment":
      return {
        label: "AMENDED",
        color: "orange",
        icon: <IconEdit size={12} />,
      };
    default:
      return {
        label: type.toUpperCase(),
        color: "gray",
        icon: <IconFileText size={12} />,
      };
  }
}

export function AddendumSection({
  caseId,
  isFinalized,
  requireAddendumFirst = false,
  reportVersion,
  onAmendmentCreated,
  onAddAddendum,
  onAddAmendment,
  onContinueAmendment,
  caseData,
}: AddendumSectionProps) {
  const [expanded, setExpanded] = useState(true);
  const [history, setHistory] = useState<SignOffHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [correctionModalOpened, setCorrectionModalOpened] = useState(false);
  const [deleteDraftConfirmationOpened, setDeleteDraftConfirmationOpened] =
    useState(false);
  const [
    deleteAmendmentDraftConfirmationOpened,
    setDeleteAmendmentDraftConfirmationOpened,
  ] = useState(false);
  const { data: addendumDraft } = useAddendumDraft(
    caseId || "",
    Boolean(caseId && isFinalized)
  );
  const deleteAddendumDraft = useDeleteAddendumDraft(caseId || "");
  const { data: amendmentDraft } = useAmendmentDraft(
    caseId || "",
    Boolean(caseId && isFinalized)
  );
  const deleteAmendmentDraft = useDeleteAmendmentDraft(caseId || "");

  const loadHistory = useCallback(async () => {
    if (!caseId || !isFinalized) return;
    setHistoryLoading(true);
    try {
      const data = await getSignOffHistory(caseId);
      setHistory(data);
    } catch {
      // silently fail — history is non-critical
    } finally {
      setHistoryLoading(false);
    }
  }, [caseId, isFinalized]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory, reportVersion]);

  const handleOpenAddendum = useCallback(() => {
    onAddAddendum?.();
  }, [onAddAddendum]);
  const handleOpenAmendment = useCallback(() => {
    onAddAmendment?.();
  }, [onAddAmendment]);
  const handleContinueAmendment = useCallback(() => {
    onContinueAmendment?.();
  }, [onContinueAmendment]);
  const handleOpenCorrection = useCallback(() => {
    setCorrectionModalOpened(true);
  }, []);
  const handleToggleExpanded = useCallback(() => {
    setExpanded((value) => !value);
  }, []);
  const handleHeaderKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      handleToggleExpanded();
    },
    [handleToggleExpanded]
  );
  const handleOpenDeleteDraftConfirmation = useCallback(() => {
    setDeleteDraftConfirmationOpened(true);
  }, []);
  const handleCloseDeleteDraftConfirmation = useCallback(() => {
    setDeleteDraftConfirmationOpened(false);
  }, []);
  const handleOpenDeleteAmendmentDraftConfirmation = useCallback(() => {
    setDeleteAmendmentDraftConfirmationOpened(true);
  }, []);
  const handleCloseDeleteAmendmentDraftConfirmation = useCallback(() => {
    setDeleteAmendmentDraftConfirmationOpened(false);
  }, []);
  const handleDeleteDraft = useCallback(async () => {
    if (!addendumDraft) return;
    try {
      await deleteAddendumDraft.mutateAsync(addendumDraft.id);
      setDeleteDraftConfirmationOpened(false);
      notifications.show({
        title: "Draft deleted",
        message: "The unsigned addendum draft was removed.",
        color: "green",
      });
    } catch {
      notifications.show({
        title: "Draft not deleted",
        message: "Refresh the case and try deleting the draft again.",
        color: "red",
      });
    }
  }, [addendumDraft, deleteAddendumDraft]);
  const handleDeleteAmendmentDraft = useCallback(async () => {
    if (!amendmentDraft) return;
    try {
      await deleteAmendmentDraft.mutateAsync(amendmentDraft.id);
      setDeleteAmendmentDraftConfirmationOpened(false);
      notifications.show({
        title: "Draft deleted",
        message: "The unsigned amendment draft was removed.",
        color: "green",
      });
    } catch {
      notifications.show({
        title: "Draft not deleted",
        message: "Refresh the case and try deleting the draft again.",
        color: "red",
      });
    }
  }, [amendmentDraft, deleteAmendmentDraft]);

  // Only the non-initial entries are shown in the section body
  const amendments = history.filter((historyItem) =>
    ["addendum", "amended", "amendment", "corrected"].includes(
      historyItem.signOffType
    )
  );
  return (
    <>
      <Paper withBorder>
        {/* Section header */}
        <Group
          justify="space-between"
          px="md"
          py="xs"
          style={{
            cursor: "pointer",
            borderBottom: expanded
              ? "1px solid var(--mantine-color-gray-3)"
              : undefined,
          }}
          onClick={handleToggleExpanded}
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          onKeyDown={handleHeaderKeyDown}
        >
          <Group gap="xs">
            <IconFileText size={16} color="var(--mantine-color-violet-6)" />
            <Text size="sm" fw={600} tt="uppercase" c="dimmed">
              Addendum / Amendment / Correction
            </Text>
            {amendments.length > 0 && (
              <Badge size="xs" variant="light" color="violet">
                {amendments.length}
              </Badge>
            )}
          </Group>
          <ActionIcon
            variant="subtle"
            size="sm"
            aria-label={expanded ? "Collapse section" : "Expand section"}
          >
            {expanded ? (
              <IconChevronUp size={14} />
            ) : (
              <IconChevronDown size={14} />
            )}
          </ActionIcon>
        </Group>

        <Collapse in={expanded}>
          <Stack gap={0} px="md" py="sm">
            {!isFinalized ? (
              <Alert
                icon={<IconAlertCircle size={14} />}
                color="gray"
                variant="light"
                radius="sm"
              >
                <Text size="xs">
                  Only available after the case is signed off
                </Text>
              </Alert>
            ) : (
              <>
                {/* Existing amendments */}
                {historyLoading ? (
                  <Loader size="xs" mb="sm" />
                ) : amendments.length > 0 ? (
                  <Timeline bulletSize={20} lineWidth={2} mb="md">
                    {amendments.map((entry) => {
                      const cfg = getTypeConfig(entry.signOffType);
                      const authorName = entry.signedBy?.fullName || "Unknown";
                      return (
                        <Timeline.Item
                          key={entry.id}
                          bullet={cfg.icon}
                          color={cfg.color}
                          title={
                            <Group gap="xs" wrap="nowrap">
                              <Badge
                                size="xs"
                                color={cfg.color}
                                variant="filled"
                              >
                                {cfg.label}
                              </Badge>
                              <Text size="xs" fw={600}>
                                {authorName}
                              </Text>
                              <Text size="xs" c="dimmed">
                                &mdash;{" "}
                                {dayjs(entry.signedAt).format(
                                  "MM/DD/YYYY h:mm A"
                                )}
                              </Text>
                            </Group>
                          }
                        >
                          {shouldShowHistoryReason(entry) && (
                            <div style={{ marginTop: 4 }}>
                              <Text size="xs" c="dimmed" span fw={500}>
                                {entry.signOffType === "addendum"
                                  ? "Name: "
                                  : "Reason: "}
                              </Text>
                              <span
                                style={{ fontSize: 12, lineHeight: 1.6 }}
                                dangerouslySetInnerHTML={{
                                  __html: resolveCommentsHtml(
                                    entry.addendumName ||
                                      entry.amendmentReason ||
                                      ""
                                  ),
                                }}
                              />
                            </div>
                          )}
                          {entry.comments && (
                            <div
                              style={{ fontSize: 12, lineHeight: 1.6 }}
                              dangerouslySetInnerHTML={{
                                __html: resolveCommentsHtml(entry.comments),
                              }}
                            />
                          )}
                        </Timeline.Item>
                      );
                    })}
                  </Timeline>
                ) : !addendumDraft && !amendmentDraft ? (
                  <Text size="sm" c="dimmed" fs="italic" mb="sm">
                    No amendments, addenda, or corrections on record
                  </Text>
                ) : null}

                {/* Action buttons */}
                {addendumDraft && (
                  <Alert color="cyan" variant="light" mb="sm">
                    <Group justify="space-between">
                      <div>
                        <Text size="xs" fw={600}>
                          Unsigned addendum draft
                        </Text>
                        <Text size="xs">{addendumDraft.name}</Text>
                      </div>
                      <Group gap="xs" wrap="nowrap">
                        <Button
                          size="xs"
                          variant="light"
                          onClick={handleOpenAddendum}
                        >
                          Continue Draft
                        </Button>
                        <Tooltip label="Delete draft">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            size="sm"
                            aria-label="Delete unsigned addendum draft"
                            onClick={handleOpenDeleteDraftConfirmation}
                          >
                            <IconTrash size={14} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Group>
                  </Alert>
                )}
                {amendmentDraft && (
                  <Alert color="orange" variant="light" mb="sm">
                    <Group justify="space-between">
                      <div>
                        <Text size="xs" fw={600}>
                          Unsigned amendment draft
                        </Text>
                        <Text size="xs">{amendmentDraft.reason}</Text>
                      </div>
                      <Group gap="xs" wrap="nowrap">
                        <Button
                          size="xs"
                          variant="light"
                          color="orange"
                          onClick={handleContinueAmendment}
                          disabled={requireAddendumFirst}
                        >
                          Continue and Sign Off Draft
                        </Button>
                        <Tooltip label="Delete draft">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            size="sm"
                            aria-label="Delete unsigned amendment draft"
                            onClick={handleOpenDeleteAmendmentDraftConfirmation}
                          >
                            <IconTrash size={14} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Group>
                  </Alert>
                )}
                <Group gap="xs" wrap="wrap">
                  <Button
                    size="xs"
                    variant="light"
                    color="cyan"
                    leftSection={<IconPlus size={12} />}
                    onClick={handleOpenAddendum}
                  >
                    {addendumDraft ? "Continue Addendum" : "Add Addendum"}
                  </Button>
                  <Button
                    size="xs"
                    variant="light"
                    color="orange"
                    leftSection={<IconEdit size={12} />}
                    onClick={handleOpenAmendment}
                    disabled={requireAddendumFirst}
                  >
                    {amendmentDraft ? "Continue Amendment" : "Add Amendment"}
                  </Button>
                  <Button
                    size="xs"
                    variant="light"
                    color="blue"
                    leftSection={<IconEdit size={12} />}
                    onClick={handleOpenCorrection}
                    disabled={requireAddendumFirst}
                  >
                    Add Correction
                  </Button>
                </Group>
                {requireAddendumFirst && (
                  <Text size="xs" c="dimmed" mt="xs">
                    Sign out the Castle addendum before starting an amendment or
                    correction.
                  </Text>
                )}
              </>
            )}
          </Stack>
        </Collapse>
      </Paper>

      {/* Correction modal — full UpdatePatientInfoModal for all fields */}
      {caseId && isFinalized && (
        <UpdatePatientInfoModal
          caseId={caseId}
          opened={correctionModalOpened}
          onClose={() => {
            setCorrectionModalOpened(false);
            loadHistory();
            onAmendmentCreated?.();
          }}
          isSignedOff
          caseData={caseData}
          correctionAmendmentMode
        />
      )}
      <ConfirmAddendumActionModal
        opened={deleteDraftConfirmationOpened}
        title="Delete unsigned addendum draft?"
        message="This removes only the unsigned draft. It does not change any signed report."
        confirmLabel="Delete Draft"
        loading={deleteAddendumDraft.isPending}
        onCancel={handleCloseDeleteDraftConfirmation}
        onConfirm={handleDeleteDraft}
      />
      <ConfirmAddendumActionModal
        opened={deleteAmendmentDraftConfirmationOpened}
        title="Delete unsigned amendment draft?"
        message="This removes only the unsigned draft. It does not change any signed report."
        confirmLabel="Delete Draft"
        loading={deleteAmendmentDraft.isPending}
        onCancel={handleCloseDeleteAmendmentDraftConfirmation}
        onConfirm={handleDeleteAmendmentDraft}
      />
    </>
  );
}
